"use client";

import { FirebaseApp, initializeApp, getApps, getApp } from "firebase/app";
import { Auth, getAuth, signInWithCustomToken, User } from "firebase/auth";
import { Database, getDatabase } from "firebase/database";
import { Messaging, getMessaging } from "firebase/messaging";

// Firebase configuration
export const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL, // important for RTDB
};

/**
 * Initialize or get existing Firebase app
 */
export const getFirebaseApp = (): FirebaseApp => {
  if (getApps().length > 0) {
    return getApp();
  }
  return initializeApp(firebaseConfig);
};

/**
 * Get Firebase Auth instance
 */
export const getFirebaseAuth = (): Auth => {
  const app = getFirebaseApp();
  return getAuth(app);
};

/**
 * Get Firebase Realtime Database instance
 */
export const getFirebaseDatabase = (): Database => {
  const app = getFirebaseApp();
  return getDatabase(app);
};

/**
 * Get Firebase Messaging instance
 */
export const getFirebaseMessaging = (): Messaging => {
  const app = getFirebaseApp();
  return getMessaging(app);
};

/**
 * Sign in with Firebase custom token
 */
export const signInWithFirebaseToken = async (token: string): Promise<User> => {
  const auth = getFirebaseAuth();
  const userCredential = await signInWithCustomToken(auth, token);
  return userCredential.user;
};

/**
 * Sanitize ID for Firebase path (remove invalid characters)
 */
export const sanitizeFirebaseId = (id: string): string => {
  return id.replace(/[.#$\\[\\]]/g, '_');
};

/**
 * Check if Firebase is initialized
 */
export const isFirebaseInitialized = (): boolean => {
  return getApps().length > 0;
};
